import { useMemo, useState } from 'react';
import { FaCheck } from 'react-icons/fa';
import { getSeriesProgressInfo, readReadingProgress } from '../utils/readingProgress';
import type { TopicMetadata } from '../types';

const TOGGLE_PULSE_MS = 180;

interface ReadingProgressMetaProps {
    topic: TopicMetadata;
    scrollPercent: number;
    completed: boolean;
    onToggleCompleted: () => void;
}

export default function ReadingProgressMeta({
    topic,
    scrollPercent,
    completed,
    onToggleCompleted,
}: ReadingProgressMetaProps) {
    const [isPulsing, setIsPulsing] = useState(false);
    const seriesInfo = useMemo(() => getSeriesProgressInfo(topic), [topic]);

    const siblingCompletion = useMemo(() => {
        const map = new Map<string, boolean>();
        seriesInfo?.parts.forEach((part) => {
            if (part.slug !== topic.slug) {
                map.set(part.slug, Boolean(readReadingProgress(part.slug)?.completed));
            }
        });
        return map;
    }, [seriesInfo, topic.slug]);

    const remainingMinutes = Math.max(0, Math.round(topic.estimatedReadTime * (1 - scrollPercent / 100)));

    const handleToggleClick = () => {
        onToggleCompleted();
        setIsPulsing(true);
        setTimeout(() => setIsPulsing(false), TOGGLE_PULSE_MS);
    };

    return (
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3 text-sm">
            {seriesInfo ? (
                <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold text-darkBlue dark:text-lightBlue">
                        Part {seriesInfo.currentPart} of {seriesInfo.totalParts}
                    </span>
                    <span className="text-gray-500 dark:text-gray-400">— {seriesInfo.name}</span>
                    <span className="flex items-center gap-1">
                        {seriesInfo.parts.map((part) => {
                            const isCurrent = part.slug === topic.slug;
                            const isPartComplete = isCurrent ? completed : siblingCompletion.get(part.slug);

                            return (
                                <span
                                    key={part.slug}
                                    title={part.title}
                                    className={`h-2 w-2 rounded-full transition-colors ${
                                        isPartComplete ? 'bg-darkBlue dark:bg-lightBlue' : 'bg-lightGrey dark:bg-gray-600'
                                    } ${isCurrent ? 'ring-2 ring-darkBlue ring-offset-1 dark:ring-lightBlue' : ''}`}
                                />
                            );
                        })}
                    </span>
                </div>
            ) : (
                <span />
            )}

            <div className="flex items-center gap-2">
                <span className="rounded-full bg-darkBlue/10 px-3 py-1 font-medium tabular-nums text-darkBlue dark:bg-lightBlue/15 dark:text-lightBlue">
                    {remainingMinutes} of {topic.estimatedReadTime} min
                </span>
                <button
                    type="button"
                    onClick={handleToggleClick}
                    aria-pressed={completed}
                    aria-label={completed ? 'Mark topic as not complete' : 'Mark topic as complete'}
                    className={`flex h-7 w-7 items-center justify-center rounded-full border transition-all duration-150 ${
                        isPulsing ? 'scale-125' : 'scale-100'
                    } ${
                        completed
                            ? 'border-darkBlue bg-darkBlue text-white dark:border-lightBlue dark:bg-lightBlue'
                            : 'border-gray-300 text-gray-400 hover:border-darkBlue hover:text-darkBlue dark:border-gray-600 dark:hover:border-lightBlue dark:hover:text-lightBlue'
                    }`}
                >
                    <FaCheck className="h-4 w-4" />
                </button>
            </div>
        </div>
    );
}
